import React from "react";
import { Box, Stack, Typography, useMediaQuery } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AutoGraphOutlinedIcon from "@mui/icons-material/AutoGraphOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

/** Contenedor hero (sticky opcional) */
export const pygHeroShellSx = (theme, { sticky = false } = {}) => ({
  ...(sticky ? { position: "sticky", top: 0, zIndex: 20 } : {}),
  flexShrink: 0,
  mb: 1.5,
  borderRadius: 3,
  overflow: "hidden",
  border: "1px solid",
  borderColor: alpha(theme.palette.divider, 0.85),
  background:
    theme.palette.mode === "dark"
      ? `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.24)} 0%, ${theme.palette.background.paper} 55%)`
      : "linear-gradient(145deg, #e8f6f8 0%, #f4f0ff 48%, #f5faf7 100%)",
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 4px 28px rgba(0,0,0,0.42)"
      : "0 8px 32px rgba(15, 23, 42, 0.07)",
  "&::before": {
    content: '""',
    display: "block",
    height: 3,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.secondary?.main || theme.palette.primary.light, 0.85)})`,
  },
});

/** Selects del toolbar */
export const pygToolbarFieldSx = (theme) => ({
  backgroundColor: alpha(theme.palette.background.paper, 0.98),
  borderRadius: 1.5,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: alpha(theme.palette.divider, 0.72),
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: alpha(theme.palette.primary.main, 0.45),
  },
});

export const pygToolbarControlSx = {
  "& .MuiInputBase-root": { height: 40 },
};

/** Campo a ancho completo en panel lateral (p. ej. Vista) */
export const pygToolbarSidebarFullRowSx = {
  gridColumn: "1 / -1",
};

const PygAnaliticoPageHeader = ({
  eyebrow = "FINANCE · PYG ANALÍTICO",
  title,
  subtitle,
  kpiChips,
  toolbar,
  navChips,
  filtroResumen,
  filtroAside,
  exportStatus,
  sticky = false,
  compact = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box sx={pygHeroShellSx(theme, { sticky })}>
      <Box sx={{ p: { xs: 1.25, sm: 1.75 } }}>
        <Stack spacing={1.25}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              alignItems: { xs: "stretch", lg: "flex-start" },
              gap: { xs: 1.25, lg: 2 },
            }}
          >
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="flex-start"
              sx={{ minWidth: 0, flex: "1 1 auto" }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.16),
                  color: theme.palette.primary.dark,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.primary.main, 0.28),
                  boxShadow: `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.5)}`,
                }}
              >
                <AutoGraphOutlinedIcon fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ lineHeight: 1.1, letterSpacing: 1.15, fontWeight: 700 }}
                >
                  {eyebrow}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.12,
                    letterSpacing: -0.02,
                    fontSize: compact
                      ? { xs: "1rem", sm: "1.05rem" }
                      : { xs: "1.05rem", md: "1.2rem" },
                  }}
                >
                  {title}
                </Typography>
                {subtitle ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.35,
                      fontSize: "0.8rem",
                      lineHeight: 1.45,
                      display: { xs: "block", md: isMobile ? "block" : "none" },
                    }}
                  >
                    {subtitle}
                  </Typography>
                ) : null}
                {kpiChips ? <Box sx={{ mt: 0.75 }}>{kpiChips}</Box> : null}
                {navChips ? (
                  <Stack
                    direction="row"
                    spacing={0.75}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ mt: 1 }}
                  >
                    {navChips}
                  </Stack>
                ) : null}
              </Box>
            </Stack>

            {toolbar ? (
              <Box
                sx={{
                  flexShrink: 0,
                  width: { xs: "100%", lg: compact ? 400 : 440 },
                  alignSelf: { lg: "flex-start" },
                }}
              >
                {toolbar}
              </Box>
            ) : null}
          </Box>

          {(filtroResumen || filtroAside) && (
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.25,
                alignItems: { xs: "stretch", sm: "center" },
                py: 1,
                px: 1.25,
                borderRadius: 2,
                bgcolor:
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.info.main, 0.12)
                    : alpha(theme.palette.info.main, 0.08),
                border: "1px solid",
                borderColor: alpha(theme.palette.info.main, 0.22),
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="flex-start"
                sx={{ flex: 1, minWidth: 0 }}
              >
                <InfoOutlinedIcon
                  sx={{
                    fontSize: 18,
                    color: "info.main",
                    flexShrink: 0,
                    mt: 0.2,
                  }}
                />
                {filtroResumen ? (
                  <Typography variant="body2" color="text.secondary">
                    {filtroResumen}
                  </Typography>
                ) : null}
              </Stack>
              {filtroAside ? (
                <Box sx={{ width: { xs: "100%", sm: 200 }, flexShrink: 0 }}>
                  {filtroAside}
                </Box>
              ) : null}
            </Box>
          )}

          {exportStatus ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                px: 0.5,
                fontStyle: exportStatus.includes("…") ? "italic" : "normal",
              }}
            >
              {exportStatus}
            </Typography>
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
};

export default PygAnaliticoPageHeader;
